<template>
  <div class="m-videoplayer-wrapper">
    <div class="m-videoplayer-container">
      <video
        ref="videoRef"
        :src="videoUrl"
        :controls="controls"
        :autoplay="autoplay"
        :loop="loop"
        :muted="muted"
        :width="width"
        :height="height"
        class="m-videoplayer"
        @play="handlePlay"
        @pause="handlePause"
        @ended="handleEnded"
        @error="handleError"
      >
        Your browser does not support the video tag.
      </video>
    </div>

    <div v-if="hint" class="m-videoplayer-hint">
      {{ hint }}
    </div>

    <div v-if="error" class="m-videoplayer-error">
      {{ error }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount } from 'vue'

interface Props {
  modelValue?: string
  controls?: boolean
  autoplay?: boolean
  loop?: boolean
  muted?: boolean
  width?: string
  height?: string
  hint?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  controls: true,
  autoplay: false,
  loop: false,
  muted: false,
  width: '100%',
  height: 'auto',
  hint: ''
})

const emit = defineEmits<{
  'play': []
  'pause': []
  'ended': []
  'error': [error: string]
}>()

const videoRef = ref<HTMLVideoElement>()
const error = ref<string>()

const videoUrl = computed(() => props.modelValue)

// Cleanup on unmount
onBeforeUnmount(() => {
  if (videoRef.value) {
    videoRef.value.pause()
    videoRef.value.src = ''
  }
})

// Watch for URL changes
watch(videoUrl, (newUrl) => {
  if (videoRef.value) {
    if (newUrl) {
      videoRef.value.load()
      if (props.autoplay) {
        videoRef.value.play().catch(err => {
          error.value = 'Failed to autoplay video'
          console.error('Video autoplay error:', err)
        })
      }
    } else {
      videoRef.value.pause()
    }
  }
})

const handlePlay = () => {
  error.value = undefined
  emit('play')
}

const handlePause = () => {
  emit('pause')
}

const handleEnded = () => {
  emit('ended')
}

const handleError = (event: Event) => {
  const errorMessage = 'Failed to load video'
  error.value = errorMessage
  emit('error', errorMessage)
}

// Public methods for external control
defineExpose({
  play: () => videoRef.value?.play(),
  pause: () => videoRef.value?.pause(),
  stop: () => {
    if (videoRef.value) {
      videoRef.value.pause()
      videoRef.value.currentTime = 0
    }
  }
})
</script>

<style scoped lang="scss">
.m-videoplayer-wrapper {
  width: 100%;
}

.m-videoplayer-container {
  width: 100%;
  background: #000;
  border-radius: 4px;
  overflow: hidden;
}

.m-videoplayer {
  width: 100%;
  height: auto;
  display: block;
}

.m-videoplayer-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.5rem;
}

.m-videoplayer-error {
  font-size: 0.75rem;
  color: #dc2626;
  margin-top: 0.25rem;
  padding: 0.5rem;
  background: #fef2f2;
  border-radius: 4px;
}
</style>

